import { dec, toDecimalString } from '../quantity/decimal.js';
import { normalizeIngredientName } from '../ingredients/normalize-name.js';
import { getUnit } from '../units/catalog.js';
import { areUnitsCompatible } from '../units/conversion.js';
/**
 * Choose a readable unit for a combined base amount: the larger of the two units when the
 * amount is at least 1 of it, otherwise the smaller one.
 */
function pickUnit(baseAmount, a, b) {
    const ua = getUnit(a);
    const ub = getUnit(b);
    const larger = dec(ua.baseFactor).gte(ub.baseFactor) ? ua : ub;
    const smaller = larger === ua ? ub : ua;
    return baseAmount.div(larger.baseFactor).gte(1) ? larger.code : smaller.code;
}
/**
 * Merge shopping items with the same normalized name and compatible units.
 * Text-only quantities ("to taste") and incompatible units stay as separate lines.
 * Order of first appearance is preserved.
 */
export function mergeShoppingItems(items) {
    const result = [];
    for (const item of items) {
        const normalizedName = normalizeIngredientName(item.name);
        const unitKey = item.unitCode ?? (item.unitText ? item.unitText.toLowerCase() : null);
        const candidate = result.find((r) => {
            if (r.normalizedName !== normalizedName)
                return false;
            if (item.quantity === null || r.quantity === null)
                return (item.quantity === null &&
                    r.quantity === null &&
                    (r.unitCode ?? r.unitText ?? null) === unitKey);
            if (item.unitCode && r.unitCode)
                return areUnitsCompatible(item.unitCode, r.unitCode);
            return (r.unitCode ?? r.unitText?.toLowerCase() ?? null) === unitKey;
        });
        if (!candidate) {
            result.push({ ...item, refs: [...item.refs], normalizedName, mergedCount: 1 });
            continue;
        }
        candidate.mergedCount += 1;
        candidate.refs.push(...item.refs);
        if (item.note && !candidate.note)
            candidate.note = item.note;
        if (item.quantityText && candidate.quantityText !== item.quantityText) {
            candidate.quantityText = candidate.quantityText
                ? `${candidate.quantityText}; ${item.quantityText}`
                : item.quantityText;
        }
        if (item.quantity === null || candidate.quantity === null)
            continue;
        if (item.unitCode && candidate.unitCode && item.unitCode !== candidate.unitCode) {
            const ui = getUnit(item.unitCode);
            const uc = getUnit(candidate.unitCode);
            const baseMin = dec(candidate.quantity)
                .times(uc.baseFactor)
                .plus(dec(item.quantity).times(ui.baseFactor));
            const baseMax = dec(candidate.quantityMax ?? candidate.quantity)
                .times(uc.baseFactor)
                .plus(dec(item.quantityMax ?? item.quantity).times(ui.baseFactor));
            const targetCode = pickUnit(baseMin, ui.code, uc.code);
            const target = getUnit(targetCode);
            candidate.unitCode = targetCode;
            candidate.quantity = toDecimalString(baseMin.div(target.baseFactor));
            candidate.quantityMax = baseMax.equals(baseMin)
                ? null
                : toDecimalString(baseMax.div(target.baseFactor));
        }
        else {
            const min = dec(candidate.quantity).plus(item.quantity);
            const max = dec(candidate.quantityMax ?? candidate.quantity).plus(item.quantityMax ?? item.quantity);
            candidate.quantity = toDecimalString(min);
            candidate.quantityMax = max.equals(min) ? null : toDecimalString(max);
        }
    }
    return result;
}
/** Subtract pantry amounts from a merged list. Items fully covered are dropped. */
export function subtractPantry(items, pantry) {
    const out = [];
    for (const item of items) {
        const key = item.normalizedName;
        const stock = pantry.filter((p) => normalizeIngredientName(p.name) === key);
        if (stock.length === 0) {
            out.push(item);
            continue;
        }
        if (item.quantity === null || !item.unitCode) {
            // Unquantified item and we have it in the pantry: assume covered.
            continue;
        }
        const unit = getUnit(item.unitCode);
        let available = dec(0);
        for (const p of stock) {
            if (p.quantity === null) {
                available = dec(Number.MAX_SAFE_INTEGER);
                break;
            }
            if (!p.unitCode || !areUnitsCompatible(p.unitCode, item.unitCode))
                continue;
            available = available.plus(dec(p.quantity).times(getUnit(p.unitCode).baseFactor));
        }
        const need = dec(item.quantity).times(unit.baseFactor);
        if (available.gte(need))
            continue;
        const remaining = need.minus(available).div(unit.baseFactor);
        out.push({ ...item, quantity: toDecimalString(remaining), quantityMax: null });
    }
    return out;
}
//# sourceMappingURL=merge.js.map